import React from 'react';
import { Download } from 'lucide-react';

import { LoadingSpinner, InlineLoadingState } from '../../../components/LoadingSpinner';

export type TemplateSignatureRequestItem = {
  id: string;
  templateId: string | null;
  templateName: string | null;
  signwellDocumentId?: string | null;
  recipientName: string;
  recipientEmail: string;
  documentName: string;
  status: string;
  createdAt: string;
  completedAt: string | null;
  hasSignedPdf: boolean;
};

const STATUS_LABELS: Record<string, string> = {
  sent: 'Gesendet',
  viewed: 'Angesehen',
  signed: 'Unterschrieben',
  completed: 'Abgeschlossen',
  declined: 'Abgelehnt',
  canceled: 'Storniert',
  expired: 'Abgelaufen',
  error: 'Fehler',
};

const STATUS_CLASSES: Record<string, string> = {
  sent: 'bg-app-surface-muted text-app-text-muted',
  viewed: 'bg-blue-50 text-blue-700',
  signed: 'bg-amber-50 text-amber-800',
  completed: 'bg-green-50 text-green-700',
  declined: 'bg-red-50 text-red-700',
  canceled: 'bg-app-surface-muted text-app-text-muted',
  expired: 'bg-orange-50 text-orange-800',
  error: 'bg-red-50 text-red-700',
};

type TemplateSignatureRequestsListProps = {
  requests: TemplateSignatureRequestItem[];
  loading: boolean;
  error: string | null;
  downloadingId: string | null;
  onDownload: (requestId: string) => void;
  onResend?: (request: TemplateSignatureRequestItem) => void;
  onRefresh: () => void;
};

function formatDate(value: string | null | undefined): string {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('de-DE');
}

export function TemplateSignatureRequestsList({
  requests,
  loading,
  error,
  downloadingId,
  onDownload,
  onResend,
  onRefresh,
}: TemplateSignatureRequestsListProps) {
  if (loading) {
    return (
      <div className="rounded-xl border border-app-border bg-app-surface p-8">
        <InlineLoadingState label="Lädt Unterschriften…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!requests.length) {
    return (
      <div className="rounded-xl border border-app-border bg-app-surface p-8 text-center text-app-text-subtle text-sm">
        Noch keine Dokumente zur Unterschrift versendet.
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-app-border bg-app-surface overflow-hidden">
      <div className="px-4 py-3 border-b border-app-border flex items-center justify-between gap-3">
        <p className="text-sm text-app-text-muted">
          Hier siehst du Dokumente, die zur Unterschrift versendet wurden, und kannst unterschriebene PDFs herunterladen.
        </p>
        <button type="button" onClick={onRefresh} className="btn-pipedrive-secondary text-xs shrink-0">
          Aktualisieren
        </button>
      </div>

      <div className="app-scrollbar hidden overflow-x-auto overscroll-contain md:block">
        <table className="min-w-full text-sm">
          <thead>
            <tr>
              <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">
                Vorlage
              </th>
              <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">
                Empfänger
              </th>
              <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">
                Status
              </th>
              <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-left text-xs font-medium text-app-text-muted">
                Gesendet
              </th>
              <th scope="col" className="sticky top-0 z-10 border-b border-app-border bg-app-surface-muted px-3 py-2.5 text-right text-xs font-medium text-app-text-muted">
                Aktion
              </th>
            </tr>
          </thead>
          <tbody>
            {requests.map((row) => {
              const statusLabel = STATUS_LABELS[row.status] ?? row.status;
              const statusClass = STATUS_CLASSES[row.status] ?? STATUS_CLASSES.sent;
              return (
                <tr key={row.id} className="border-b border-app-border">
                  <td className="px-3 py-2 text-app-text">{row.templateName || row.documentName}</td>
                  <td className="px-3 py-2 text-app-text-muted">
                    <div>{row.recipientName}</div>
                    <div className="text-xs text-app-text-subtle">{row.recipientEmail}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass}`}>
                      {statusLabel}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-app-text-muted">{formatDate(row.createdAt)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center justify-end gap-2">
                      {row.status === 'error' && onResend && row.signwellDocumentId ? (
                        <button
                          type="button"
                          onClick={() => onResend(row)}
                          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-pipedrive-blue hover:bg-blue-50"
                        >
                          Erneut senden
                        </button>
                      ) : null}
                      {row.hasSignedPdf ? (
                        <button
                          type="button"
                          onClick={() => onDownload(row.id)}
                          disabled={downloadingId === row.id}
                          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-pipedrive-blue hover:bg-blue-50 disabled:opacity-50"
                        >
                          {downloadingId === row.id ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <Download className="w-3.5 h-3.5" />
                          )}
                          PDF
                        </button>
                      ) : row.status !== 'error' ? (
                        <span className="text-xs text-app-text-subtle">-</span>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="md:hidden divide-y divide-app-border">
        {requests.map((row) => {
          const statusLabel = STATUS_LABELS[row.status] ?? row.status;
          const statusClass = STATUS_CLASSES[row.status] ?? STATUS_CLASSES.sent;
          return (
            <div key={row.id} className="px-4 py-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-app-text truncate">{row.templateName || row.documentName}</p>
                  <p className="text-xs text-app-text-subtle">{row.recipientName} · {row.recipientEmail}</p>
                </div>
                <span className={`shrink-0 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass}`}>
                  {statusLabel}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs text-app-text-subtle">
                <span>{formatDate(row.createdAt)}</span>
                <div className="inline-flex items-center gap-3">
                  {row.status === 'error' && onResend && row.signwellDocumentId ? (
                    <button
                      type="button"
                      onClick={() => onResend(row)}
                      className="text-pipedrive-blue font-medium"
                    >
                      Erneut senden
                    </button>
                  ) : null}
                  {row.hasSignedPdf ? (
                    <button
                      type="button"
                      onClick={() => onDownload(row.id)}
                      disabled={downloadingId === row.id}
                      className="inline-flex items-center gap-1 text-pipedrive-blue font-medium disabled:opacity-50"
                    >
                      {downloadingId === row.id ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <Download className="w-3.5 h-3.5" />
                      )}
                      PDF herunterladen
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
